import { Box, Divider, Tab } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceHeading } from '~/components/SolaForceHeading';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import { AbsenceType } from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useEmployeeAbsenceLimitedData } from '~/data/absences/useEmployeeAbsenceLimitedData';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { AbsencesTable } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable';
import { PeopleModuleBasicDataAccessMode } from '~/scenes/PeopleModule/components/Tabs/BasicTab';

import { AbsencesCalendar } from './components/AbsencesCalendar/component';
import { VacationBalanceData } from './components/VacationBalanceData';
import { useSxStyles } from './useStyles';

type PropsType = {
  accessMode: PeopleModuleBasicDataAccessMode;
};

export const AbsencesTab = (props: PropsType) => {
  const { accessMode } = props;

  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  if (
    accessMode !== PeopleModuleBasicDataAccessMode.Full &&
    hasRoleAccessOverEmployee([Roles.SUBSTITUTE_ABSENCE_MANAGER])
  ) {
    return <AbsencesTabFullAccess />;
  }

  switch (accessMode) {
    case PeopleModuleBasicDataAccessMode.Full:
      return <AbsencesTabFullAccess />;
    default:
      return <AbsencesTabLimitedAccess />;
  }
};

export const AbsencesTabFullAccess = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { absenceEntries } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const [selectedTab, setSelectedTab] = useState<AbsenceType>(
    AbsenceType.SHORT,
  );

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case AbsenceType.LONG:
        return t('laLongTermAbsences');
      case AbsenceType.SHORT:
        return t('laShortTermAbsences');
      default:
        return '';
    }
  };

  return (
    <>
      <SolaForceTabs
        value={Object.keys(AbsenceType).indexOf(selectedTab)}
        onChange={(_event, value) => {
          const tab = Object.keys(AbsenceType)[value];

          if (!!tab) {
            setSelectedTab(tab as AbsenceType);
          }
        }}
        background={'fade'}
        sx={sxStyles.tabsOffset}
      >
        {Object.keys(AbsenceType).map((type, index) => (
          <Tab key={index} label={getTabLabel(type)} data-cy={type} />
        ))}
      </SolaForceTabs>

      <Box sx={sxStyles.contentTabs}>
        {selectedTab === AbsenceType.SHORT && (
          <Box sx={sxStyles.activeContent}>
            <VacationBalanceData />
            <Divider sx={sxStyles.divider} />
            <AbsencesTable
              absenceType={AbsenceType.SHORT}
              employeeId={selectedEmployeeId}
            />
          </Box>
        )}
        {selectedTab === AbsenceType.LONG && (
          <Box sx={sxStyles.activeContent}>
            <AbsencesTable
              absenceType={AbsenceType.LONG}
              employeeId={selectedEmployeeId}
            />
          </Box>
        )}
        <Divider sx={sxStyles.divider} />
        <SolaForceHeading heading={t('laAbsences')} />
        <AbsencesCalendar
          absenceEntries={absenceEntries}
          employeeId={selectedEmployeeId}
          containerWidth={500}
          shouldRedirect={false}
        />
      </Box>
    </>
  );
};

export const AbsencesTabLimitedAccess = () => {
  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { absenceEntries } = useEmployeeAbsenceLimitedData({
    employeeId: selectedEmployeeId,
  });

  return (
    <Box>
      <SolaForceHeading heading={t('laVacationsAndAbsences')} />
      <AbsencesCalendar
        absenceEntries={absenceEntries}
        employeeId={selectedEmployeeId}
        containerWidth={500}
        shouldRedirect={false}
      />
    </Box>
  );
};
